import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  SurveyInput,
  useSaveNewSurveyMutation,
  useSaveExistingSurveyMutation,
  useToggleSurveyArchiveMutation,
  useCloneSurveyMutation,
} from 'core/graphql';
import { GlobalToastTypeNames, ToastTypes } from 'types';

import { useToasts } from '../Toast/useToasts';
import { surveyErrorMessage } from './surveyModel';

interface ISurveyMutationsHook {
  /** Create (`id` absent) or full-form update; resolves to the survey id or null. */
  save: (input: SurveyInput, id?: string) => Promise<string | null>;
  /** Archive / restore a template. */
  archive: (id: string, isArchived: boolean) => Promise<boolean>;
  /** Deep-copy questions (no assignments); resolves to the copy's id or null. */
  duplicate: (id: string) => Promise<string | null>;
  pending: boolean;
}

/**
 * Spec 0011 — constructor mutations (`createSurvey` / `updateSurvey` /
 * `archiveSurvey` / `duplicateSurvey`) behind the house fire-and-toast
 * pattern: failures surface the mapped `customErrors` code
 * (SURVEY_LOCKED, ARCHIVED, INVALID_QUESTION, …) as an error toast; success
 * toasts are left to the caller, which knows the follow-up navigation.
 */
export const useSurveyMutations = (
  onChanged?: () => void,
): ISurveyMutationsHook => {
  const { t } = useTranslation();
  const { addToast } = useToasts();
  const [createRun] = useSaveNewSurveyMutation();
  const [updateRun] = useSaveExistingSurveyMutation();
  const [archiveRun] = useToggleSurveyArchiveMutation();
  const [duplicateRun] = useCloneSurveyMutation();
  const [pending, setPending] = useState(false);

  const toastError = useCallback(
    (title: string, text?: string) => {
      addToast({
        type: GlobalToastTypeNames.Survey,
        data: { type: ToastTypes.error, title, text },
      });
    },
    [addToast],
  );

  const save: ISurveyMutationsHook['save'] = useCallback(
    async (input, id) => {
      setPending(true);
      try {
        if (id) {
          const result = await updateRun({ variables: { id, input } });
          const payload = result.data?.updateSurvey;
          if (payload?.success && payload.survey) return payload.survey.id;
          toastError(
            t('pages.surveys.builder.toastSaveError'),
            surveyErrorMessage(t, payload?.customErrors),
          );
          return null;
        }
        const result = await createRun({ variables: { input } });
        const payload = result.data?.createSurvey;
        if (payload?.success && payload.survey) return payload.survey.id;
        toastError(
          t('pages.surveys.builder.toastSaveError'),
          surveyErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.surveys.builder.toastSaveError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [createRun, updateRun, toastError, t],
  );

  const archive: ISurveyMutationsHook['archive'] = useCallback(
    async (id, isArchived) => {
      setPending(true);
      try {
        const result = await archiveRun({ variables: { id, isArchived } });
        const payload = result.data?.archiveSurvey;
        if (payload?.success) {
          onChanged?.();
          return true;
        }
        toastError(
          t('pages.surveys.list.toastArchiveError'),
          surveyErrorMessage(t, payload?.customErrors),
        );
        return false;
      } catch (e) {
        toastError(
          t('pages.surveys.list.toastArchiveError'),
          (e as Error).message,
        );
        return false;
      } finally {
        setPending(false);
      }
    },
    [archiveRun, onChanged, toastError, t],
  );

  const duplicate: ISurveyMutationsHook['duplicate'] = useCallback(
    async (id) => {
      setPending(true);
      try {
        const result = await duplicateRun({ variables: { id } });
        const payload = result.data?.duplicateSurvey;
        if (payload?.success && payload.survey) {
          onChanged?.();
          return payload.survey.id;
        }
        toastError(
          t('pages.surveys.list.toastDuplicateError'),
          surveyErrorMessage(t, payload?.customErrors),
        );
        return null;
      } catch (e) {
        toastError(
          t('pages.surveys.list.toastDuplicateError'),
          (e as Error).message,
        );
        return null;
      } finally {
        setPending(false);
      }
    },
    [duplicateRun, onChanged, toastError, t],
  );

  return { save, archive, duplicate, pending };
};
